import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, ChevronRight, ShieldCheck, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { submitSmsLead } from "@/lib/api";
import Seo from "@/components/Seo";

const CASE_TYPES = [
  { id: "Auto Accidents", label: "Car Accident" },
  { id: "Personal Injury", label: "Personal Injury" },
  { id: "Workplace Injury", label: "Workplace Injury" },
  { id: "Employment", label: "Employment Issue" },
  { id: "Other", label: "Other legal matter" },
];

const TIMEFRAMES = [
  { id: "today", label: "Today" },
  { id: "this_week", label: "This week" },
  { id: "this_month", label: "This month" },
  { id: "earlier", label: "Earlier" },
];

const TOTAL_STEPS = 3;

function ProgressBar({ step }) {
  const pct = Math.round((step / TOTAL_STEPS) * 100);
  return (
    <div data-testid="sms-progress" className="w-full">
      <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-semibold text-slate-500 mb-2">
        <span>Step {step} of {TOTAL_STEPS}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#C5A059] transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function PhoneStep({ phone, setPhone, onNext, submitting }) {
  // Step 1 — single field above the fold. The minimum viable conversion.
  const valid = phone.replace(/\D/g, "").length >= 7;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0A192F] leading-tight">
          Need a Lawyer?
        </h1>
        <p className="mt-3 text-base text-slate-700 leading-relaxed">
          Get a free case review and connect with attorneys who handle cases like yours.
        </p>
        <p className="mt-2 text-sm text-[#0A192F] font-medium">
          ✔ Free &nbsp;•&nbsp; No obligation &nbsp;•&nbsp; Confidential
        </p>
      </div>
      <div>
        <label
          htmlFor="sms-phone"
          className="block text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-2"
        >
          Your phone number
        </label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            data-testid="sms-phone-input"
            id="sms-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="pl-11 h-14 text-lg rounded-xl border-slate-300 focus-visible:ring-[#C5A059]"
          />
        </div>
        <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
          <Lock className="w-3 h-3" /> We don't sell or share your number.
        </p>
      </div>
      <Button
        data-testid="sms-step1-continue"
        onClick={onNext}
        disabled={!valid || submitting}
        className="w-full h-14 text-base font-semibold rounded-xl bg-[#0A192F] hover:bg-[#112A46] text-white disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <>
            CONTINUE <ChevronRight className="w-5 h-5 ml-1" />
          </>
        )}
      </Button>
    </div>
  );
}

function NameEmailStep({ name, setName, email, setEmail, onNext }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#0A192F]">
          Who should the attorney contact?
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Optional — you can skip this and we'll just use your phone.
        </p>
      </div>
      <div>
        <label
          htmlFor="sms-name"
          className="block text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-1.5"
        >
          Full Name
        </label>
        <Input
          data-testid="sms-name-input"
          id="sms-name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-12 rounded-lg border-slate-300"
        />
      </div>
      <div>
        <label
          htmlFor="sms-email"
          className="block text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-1.5"
        >
          Email
        </label>
        <Input
          data-testid="sms-email-input"
          id="sms-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12 rounded-lg border-slate-300"
        />
      </div>
      <Button
        data-testid="sms-step2-continue"
        onClick={onNext}
        className="w-full h-14 text-base font-semibold rounded-xl bg-[#0A192F] hover:bg-[#112A46] text-white"
      >
        CONTINUE CASE REVIEW <ChevronRight className="w-5 h-5 ml-1" />
      </Button>
    </div>
  );
}

function QualifyStep({ caseType, setCaseType, when, setWhen, onSubmit, submitting }) {
  const ready = caseType && when;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#0A192F]">
          Tell us a little about your case
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Two quick taps. No typing required.
        </p>
      </div>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-2">
          What type of case?
        </p>
        <div className="grid grid-cols-1 gap-2">
          {CASE_TYPES.map((c) => (
            <button
              key={c.id}
              data-testid={`sms-case-${c.id}`}
              type="button"
              onClick={() => setCaseType(c.id)}
              className={`text-left h-12 px-4 rounded-lg border transition flex items-center justify-between ${
                caseType === c.id
                  ? "border-[#C5A059] bg-[#FFF8E7] text-[#0A192F] font-semibold"
                  : "border-slate-300 text-slate-700 hover:border-slate-400"
              }`}
            >
              <span>{c.label}</span>
              {caseType === c.id && (
                <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
              )}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-2">
          When did it happen?
        </p>
        <div className="grid grid-cols-2 gap-2">
          {TIMEFRAMES.map((t) => (
            <button
              key={t.id}
              data-testid={`sms-when-${t.id}`}
              type="button"
              onClick={() => setWhen(t.id)}
              className={`h-12 rounded-lg border transition font-medium ${
                when === t.id
                  ? "border-[#C5A059] bg-[#FFF8E7] text-[#0A192F]"
                  : "border-slate-300 text-slate-700 hover:border-slate-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <Button
        data-testid="sms-step3-submit"
        onClick={onSubmit}
        disabled={!ready || submitting}
        className="w-full h-14 text-base font-semibold rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "SUBMIT FOR REVIEW"}
      </Button>
    </div>
  );
}

function ThankYouStep({ caseId }) {
  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#FFF8E7] border-2 border-[#C5A059] flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-[#C5A059]" />
      </div>
      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#0A192F]">
          Got it — thanks.
        </h2>
        <p className="mt-3 text-sm text-slate-700 leading-relaxed">
          Attorneys in our network review new cases daily. If they believe they
          can help, they may contact you directly.
        </p>
      </div>
      <div className="p-4 rounded-xl bg-[#FFF8E7] border border-[#C5A059]/40 text-left">
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-1">
          Want to add more detail?
        </p>
        <p className="text-sm text-slate-700 mb-3">
          A 60-second chat with our AI helps attorneys evaluate your case faster.
        </p>
        <Link
          to={`/intake?case_id=${caseId}`}
          data-testid="sms-continue-to-lexi"
        >
          <Button className="w-full h-12 rounded-lg bg-[#0A192F] hover:bg-[#112A46] text-white font-semibold">
            Add details with Lexi
          </Button>
        </Link>
      </div>
      <div className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Your information is encrypted and confidential.
      </div>
    </div>
  );
}

export default function SmsFunnelPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [caseType, setCaseType] = useState("");
  const [when, setWhen] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [caseId, setCaseId] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = await submitSmsLead({
        phone,
        name: name || undefined,
        email: email || undefined,
        case_type: caseType || undefined,
        when_happened: when || undefined,
      });
      setCaseId(data.case_id);
      setStep(4);
      toast.success("Submitted — an attorney may reach out shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const trustLine = useMemo(() => (
    <div className="mt-6 p-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] tracking-[0.16em] uppercase font-semibold text-slate-600 flex items-center justify-center gap-4">
      <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]" /> Secure</span>
      <span>•</span>
      <span>No spam</span>
      <span>•</span>
      <span>No obligation</span>
    </div>
  ), []);

  return (
    <div data-testid="sms-funnel-page" className="min-h-screen bg-white">
      <Seo
        title="Need a Lawyer? Free Case Review — LexiNetwork"
        description="Submit your case in under 60 seconds. Free, confidential review. Connect with attorneys handling car accidents, personal injury, employment and more."
        path="/sms"
      />
      {/* Tight, mobile-first column. No nav, no footer — one action per screen. */}
      <div className="max-w-md mx-auto px-5 pt-8 pb-32">
        {/* Tiny brand mark only — keeps trust without nav distraction */}
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg font-bold text-[#0A192F] tracking-tight">
            LexiNetwork
          </Link>
          <Link
            to="/"
            data-testid="sms-learn-more"
            className="text-xs text-slate-500 underline underline-offset-4 hover:text-[#0A192F]"
          >
            Learn more
          </Link>
        </div>

        {step <= 3 && (
          <div className="mb-6">
            <ProgressBar step={step} />
          </div>
        )}

        {step === 1 && (
          <PhoneStep
            phone={phone}
            setPhone={setPhone}
            onNext={() => setStep(2)}
            submitting={false}
          />
        )}
        {step === 2 && (
          <NameEmailStep
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <QualifyStep
            caseType={caseType}
            setCaseType={setCaseType}
            when={when}
            setWhen={setWhen}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        )}
        {step === 4 && <ThankYouStep caseId={caseId} />}

        {step <= 3 && trustLine}

        {/* Money + outcome framing — only on the first screen so it doesn't
            clutter the tap-flow on later steps. */}
        {step === 1 && (
          <div className="mt-8 space-y-4">
            <div>
              <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#C5A059]">
                Why people submit
              </p>
              <ul className="mt-2 space-y-2 text-sm text-slate-700 leading-relaxed">
                <li className="flex gap-2">
                  <span className="text-[#C5A059] mt-1.5 w-1 h-1 rounded-full bg-[#C5A059] shrink-0" />
                  <span>Many cases may involve compensation for medical bills, lost wages and damages.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#C5A059] mt-1.5 w-1 h-1 rounded-full bg-[#C5A059] shrink-0" />
                  <span>Attorneys evaluate eligibility for free.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#C5A059] mt-1.5 w-1 h-1 rounded-full bg-[#C5A059] shrink-0" />
                  <span>Submission takes under 60 seconds.</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#C5A059]">
                Cases we cover
              </p>
              <p className="mt-2 text-sm text-slate-700">
                Car Accidents · Personal Injury · Workplace Injury · Employment · Other legal matters
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom CTA on small screens — only when the user is on a
          step with a continue action and hasn't already pressed it. Keeps
          conversion-critical CTA always one tap away. */}
      {step === 1 && (
        <div
          data-testid="sms-sticky-cta"
          className="fixed bottom-0 inset-x-0 sm:hidden bg-white/95 backdrop-blur border-t border-slate-200 px-5 py-3 z-40"
        >
          <Button
            onClick={() => {
              const el = document.getElementById("sms-phone");
              if (el) el.focus();
              else navigate("#sms-phone");
            }}
            className="w-full h-12 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-bold tracking-wide"
          >
            GET MY FREE CASE REVIEW
          </Button>
        </div>
      )}
    </div>
  );
}
